"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/shared/lib/analytics";

/**
 * "어느 글의 어느 대목에서 얼마나 머물렀고, 다음에 어디로 갔나"를 재는 계측기.
 *
 * GA4 향상된 측정이 자동으로 주는 건 page_view · 이탈 클릭 · 스크롤 90% 1회뿐이라,
 * 읽기형 콘텐츠에서 정작 궁금한 것들이 빈다. 여기서 그 넷을 보탠다.
 *
 * | 이벤트 | 답하는 질문 |
 * |---|---|
 * | `scroll_depth`       | 어디까지 읽고 이탈했나 (25·50·75·100%) |
 * | `page_engagement`    | 이 글에 얼마나 머물렀나 (탭이 실제로 보인 시간만) |
 * | `section_engagement` | **여섯 절 중 어디서 오래 멈췄나** |
 * | `internal_nav`       | 읽고 나서 어디로 갔나 (nav_from → nav_to) |
 *
 * `page_engagement` 를 따로 두는 이유: GA4 기본 참여 시간은 **세션의 마지막 페이지**를
 * 제대로 못 센다(끝을 알리는 다음 이벤트가 없어서). 글 하나 읽고 나가는 사이트에선
 * 그게 대부분이라 기본 지표만 보면 체류가 통째로 과소 집계된다.
 *
 * 위젯은 건드리지 않는다 — 링크는 document 위임 클릭으로, 절은 DOM 구조(`section[id]`)로
 * 잡으므로 콘텐츠가 늘어도 계측이 따라온다.
 */
export function EngagementTracker() {
  const $ = _c(3);
  const pathname = usePathname();
  const pathRef = useRef(pathname);
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      pathRef.current = pathname;
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  useScrollDepth(pathname);
  usePageEngagement(pathname);
  useSectionEngagement(pathname);
  useInternalNav(pathRef);
  return null;
}

/** 25·50·75·100% 도달을 한 번씩. 어느 지점에서 독자가 떨어져 나가는지 본다. */
function useScrollDepth(pathname) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      const milestones = [25, 50, 75, 100];
      const fired = new Set();
      const onScroll = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) {
          return;
        }
        const percent = Math.min(100, Math.round((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100));
        for (const milestone of milestones) {
          if (percent >= milestone && !fired.has(milestone)) {
            fired.add(milestone);
            trackEvent("scroll_depth", {
              page_path: pathname,
              scroll_percent: milestone
            });
          }
        }
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      onScroll();
      return () => window.removeEventListener("scroll", onScroll);
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

/**
 * 탭이 실제로 보이던 시간을 누적해 보낸다.
 *
 * **한 번만 보내지 않고 구간마다 나눠 보낸다.** 숨김 시점에 한 번 보내고 끝내면
 * "10초 읽기 → 앱 전환 → 복귀 후 30초"가 10초로 기록된다. GA4 자체 engagement_time_msec
 * 도 같은 방식(증분 전송)이라, 보고서에서는 `engaged_seconds` 를 **합계**로 본다.
 * 초 미만 나머지는 버리지 않고 다음 구간으로 이월한다.
 */
function usePageEngagement(pathname) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      let visibleSince = document.visibilityState === "visible" ? Date.now() : 0;
      let accumulated = 0;
      const stopCounting = () => {
        if (!visibleSince) {
          return;
        }
        accumulated = accumulated + (Date.now() - visibleSince);
        accumulated;
        visibleSince = 0;
      };
      const report = () => {
        stopCounting();
        const seconds = Math.floor(accumulated / 1000);
        if (seconds < 1) {
          return;
        }
        accumulated = accumulated - seconds * 1000;
        accumulated;
        trackEvent("page_engagement", {
          page_path: pathname,
          engaged_seconds: seconds
        });
      };
      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          visibleSince = Date.now();
        } else {
          report();
        }
      };
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", report);
      return () => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pagehide", report);
        report();
      };
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

/**
 * 절 단위 체류.
 *
 * "화면 한가운데 30% 띠에 걸쳐 있으면 읽는 중"으로 본다. 교차 비율(threshold)로 판정하면
 * 뷰포트보다 긴 절은 비율이 영영 기준을 못 넘어 통째로 누락된다 — 절 길이에 안 휘둘리는
 * 기준이 필요해서 rootMargin 으로 판정 띠를 좁혔다.
 */
function useSectionEngagement(pathname) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== pathname) {
    t0 = () => {
      const sections = Array.from(document.querySelectorAll("section[id^='section-']"));
      if (!sections.length) {
        return;
      }
      const readTitle = _temp;
      const state = new Map(sections.map(element_0 => [element_0.id, {
        title: readTitle(element_0),
        since: 0,
        total: 0
      }]));
      const onScreen = new Set();
      const pause = id => {
        const entry = state.get(id);
        if (!entry?.since) {
          return;
        }
        entry.total = entry.total + (Date.now() - entry.since);
        entry.since = 0;
      };
      const resume = id_0 => {
        const entry_0 = state.get(id_0);
        if (!entry_0 || entry_0.since) {
          return;
        }
        entry_0.since = Date.now();
      };
      const flush = (id_1, t2) => {
        const final = t2 === undefined ? false : t2;
        const entry_1 = state.get(id_1);
        if (!entry_1) {
          return;
        }
        pause(id_1);
        const seconds = Math.floor(entry_1.total / 1000);
        if (seconds < (final ? 1 : 2)) {
          return;
        }
        entry_1.total = entry_1.total - seconds * 1000;
        trackEvent("section_engagement", {
          page_path: pathname,
          section_id: id_1,
          section_title: entry_1.title,
          engaged_seconds: seconds
        });
      };
      const observer = new IntersectionObserver(entries => {
        for (const entry_2 of entries) {
          const id_2 = (entry_2.target as HTMLElement).id;
          if (entry_2.isIntersecting) {
            onScreen.add(id_2);
            if (document.visibilityState === "visible") {
              resume(id_2);
            }
          } else {
            onScreen.delete(id_2);
            flush(id_2);
          }
        }
      }, {
        rootMargin: "-35% 0px -35% 0px",
        threshold: 0
      });
      for (const element_1 of sections) {
        observer.observe(element_1);
      }
      const onVisibility = () => {
        const apply = document.visibilityState === "visible" ? resume : pause;
        onScreen.forEach(apply);
      };
      const flushAll = () => state.forEach((_, id_3) => flush(id_3, true));
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", flushAll);
      return () => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pagehide", flushAll);
        observer.disconnect();
        flushAll();
      };
    };
    t1 = [pathname];
    $[0] = pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

/** 내부 이동의 from → to. 위젯을 건드리지 않으려고 document 위임 클릭을 쓴다. */
function _temp(element) {
  const labelledBy = element.getAttribute("aria-labelledby");
  const heading = labelledBy ? document.getElementById(labelledBy) : null;
  return (heading?.getAttribute("aria-label") ?? heading?.textContent ?? "").trim().slice(0, 80);
}
function useInternalNav(pathRef) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== pathRef) {
    t0 = () => {
      const onClick = event => {
        const anchor = (event.target as HTMLElement | null)?.closest?.("a");
        if (!anchor) {
          return;
        }
        const href = anchor.getAttribute("href");
        if (!href?.startsWith("/")) {
          return;
        }
        trackEvent("internal_nav", {
          nav_from: pathRef.current,
          nav_to: href,
          link_text: (anchor.textContent ?? "").trim().slice(0, 80)
        });
      };
      document.addEventListener("click", onClick, {
        capture: true
      });
      return () => document.removeEventListener("click", onClick, {
        capture: true
      });
    };
    t1 = [pathRef];
    $[0] = pathRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}